/* eslint-disable */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { chain, sortBy } from 'lodash';
import moment from 'moment';
import DOMPurify from 'isomorphic-dompurify';
import { Avatar, AvatarGroup, Box, IconButton, Tooltip, Typography } from '@mui/material';
import VideocamOffOutlinedIcon from '@mui/icons-material/VideocamOffOutlined';
import './style.scss';

import { ExpertHubLayout } from 'components/templates';
import { SecondaryBadge } from 'components/molecules/custom-badges';
import { schedulesActions } from 'pages/schedules/slice';
import {
  ClosableModal,
  CustomModal,
  Loader,
  PrimaryLoadingButton,
  SecondaryButton,
  SecondaryLoadingButton,
} from 'components';
import { selectAuthUser } from 'base/auth/selectors';
import { toHoursAndMinutes, monthDayFormat, timeFormat } from 'utils/formats';
import { selectEnterprise } from 'base/core/selectors';
import { getCalendarConfig, getCurrentTenant } from 'utils/common-utils';
import { ArrowCircleLeft, ArrowCircleRight } from '../../assets/icons';
import { selectSchedule, selectScheduleStateLoader, selectSessionUrl } from './selector';
import { ActiveIndexObject, IRecording, ISchedule, IScheduleGroupByDate } from './interface';
import SchedulesListItem from './components/schedules-view/components/list-item';
import { useStickyBox } from 'react-sticky-box';
import { AddToCalendarModal, useConfirmation } from 'base/common';
import { SessionType } from 'pages/sessions/interface';

const avatarStyle = {
  width: 32,
  height: 32,
};
const labelStyle = {
  marginBottom: 1,
  color: 'text.secondary',
};
const progressBgStyle = {
  backgroundColor: 'secondary.light',
  height: 28,
  borderRadius: 1.5,
};
const progressStyle = {
  backgroundColor: 'secondary.dark',
  borderTopLeftRadius: 6,
  borderBottomLeftRadius: 6,
  height: '100%',
};

const SchedulesView = () => {
  const dispatch = useDispatch();
  const confirm = useConfirmation();

  const subTitle = moment().format('dddd, MMMM Do, YYYY');
  const [verticleOffset, setVerticleOffset] = useState(204);
  const stickyRef = useStickyBox({ offsetTop: verticleOffset });
  const [activeIndexObj, setActiveIndexObj] = useState<ActiveIndexObject>({
    parentIndex: 0,
    itemIndex: 0,
  });
  const [title, setTitle] = useState('');
  const [activeMonthIndex, setActiveMonthIndex] = useState<number>(0); // initial 0
  const schedulesData: ISchedule[] = useSelector(selectSchedule);
  const [activeSchedule, setActiveSchedule] = useState<ISchedule>(schedulesData[0]);
  const [groupedSchedules, setGroupedSchedules] = useState<IScheduleGroupByDate[]>([]);
  const [mostSessionDay, setMostSessionDay] = useState<string>('');
  const [totalDuration, setTotalDuration] = useState<number>(0);
  const [avarageDuration, setAvarageDuration] = useState<number>(0);
  const [spentDuration, setSpentDuration] = useState<number>(0);
  // add to caledar modal
  const [openCalendarModal, setOpenCalendarModal] = useState<boolean>(false);
  // add to caledar config
  const [calendarConfig, setCalendarConfig] = useState<any>({});

  const joinAction = useMemo(() => {
    if (!activeSchedule) {
      return { ctaText: '--', isDisabled: true };
    }

    if (activeSchedule.isCancel) {
      return { ctaText: 'Cancelled', isDisabled: true };
    }

    if (
      (activeSchedule.sessionType !== SessionType.liveClass &&
        moment().isAfter(moment(activeSchedule.meta.slot.to))) ||
      (activeSchedule.sessionType === SessionType.liveClass &&
        moment().isAfter(
          moment(activeSchedule.meta.slot.to).add({
            hours: 6,
          }),
        ))
    ) {
      return {
        ctaText: 'Ended',
        isDisabled: true,
      };
    }

    if (
      ((activeSchedule.sessionType !== SessionType.liveClass &&
        moment().isSameOrBefore(moment(activeSchedule.meta.slot.to))) ||
        (activeSchedule.sessionType === SessionType.liveClass &&
          moment().isSameOrBefore(
            moment(activeSchedule.meta.slot.to).add({
              hours: 6,
            }),
          ))) &&
      moment().isSameOrAfter(moment(activeSchedule.meta.slot.from).subtract(5, 'minutes'))
    ) {
      return {
        ctaText: 'Join session',
        isDisabled: false,
      };
    }

    return { ctaText: 'Upcoming', isDisabled: true };
  }, [activeSchedule]);

  useEffect(() => {
    const grouped = chain(schedulesData)
      .sortBy((item: ISchedule) => item.from)
      .groupBy('date')
      .map((value, key) => ({ date: key, events: value }))
      .value();
    const groupSorted: IScheduleGroupByDate[] = sortBy(grouped, (item: IScheduleGroupByDate) => item.date);

    setGroupedSchedules(groupSorted);
  }, [schedulesData]);

  const [isUrlLoading, setIsUrlLoading] = useState<boolean>(false);
  const url = useSelector(selectSessionUrl);
  const enterprise = useSelector(selectEnterprise);
  const authUser = useSelector(selectAuthUser);
  const loading = useSelector(selectScheduleStateLoader);
  const [open, setOpen] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };
  useEffect(() => {
    if (url) {
      setIsUrlLoading(false);
      setTimeout(() => {
        window.open(url, '_blank');
      });
      dispatch(schedulesActions.clearSessionUrl());
    }
  }, [dispatch, url]);

  const loadUrl = () => {
    setIsUrlLoading(true);
    try {
      if (activeSchedule?.meetingId) {
        const tenant = getCurrentTenant();
        const payload = {
          entId: enterprise.id,
          entUrl: tenant,
          sessionId: activeSchedule?.id,
          expertId: authUser.id,
          name: authUser?.firstName,
          role: 'expert',
          sessionType: activeSchedule?.sessionType,
          title: activeSchedule?.title,
          isObserverAllowed: activeSchedule?.isObserverAllowed,
          meetingId: activeSchedule?.meetingId,
          firstName: authUser?.firstName ?? '-',
          lastName: authUser?.lastName ?? '-',
          userId: authUser.id,
          profileImgUrl: authUser?.profileImgUrl ?? '-',
          email: authUser?.email ?? '-',
        };

        dispatch(schedulesActions.getSessionUrl(payload));
      } else {
        confirm({
          variant: 'info',
          icon: 'error',
          message: 'Meeting id is empty',
          confirmButtonTxt: 'Try again',
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('error==>', error);
      confirm({
        variant: 'info',
        icon: 'error',
        message: 'Sorry, Could not able to joing the meeting',
        confirmButtonTxt: 'Try again',
      });
    }
  };

  useEffect(() => {
    setTitle(moment().subtract(activeMonthIndex, 'months').format('MMMM, YYYY'));
    const dateFrom = moment()
      .subtract(activeMonthIndex, 'months')
      .startOf('month')
      .format('YYYY-MM-DD');
    const dateTo = moment()
      .subtract(activeMonthIndex, 'months')
      .endOf('month')
      .format('YYYY-MM-DD');

    const payload = {
      from: dateFrom,
      to: dateTo,
    };

    dispatch(schedulesActions.getScheduleList(payload));
  }, [dispatch, activeMonthIndex]);

  // cancel session modal
  const [openCancelModel, setCancelModelOpen] = useState<boolean>(false);
  const handleCancelModelOpen = () => {
    setCancelModelOpen(true);
  };

  const handleCancelModelClose = () => {
    setCancelModelOpen(false);
  };

  const handleActive = (index: number, itemIndex: number) => {
    setActiveIndexObj({ parentIndex: index, itemIndex });
    if (window.matchMedia("(min-width: 992px)").matches) {
      setOpen(false);
    }
    else {
      setOpen(true);
    }
  };

  const handleMonth = (addition: number) => {
    const val = activeMonthIndex + addition;

    if (val >= 3 || val <= -3) {
      return;
    }
    setActiveMonthIndex(val);
  };

  const init = useCallback(() => {
    //set active schedule details
    if (
      groupedSchedules[activeIndexObj.parentIndex] &&
      groupedSchedules[activeIndexObj.parentIndex].events[activeIndexObj.itemIndex]
    ) {
      const session: ISchedule = groupedSchedules[activeIndexObj.parentIndex].events[activeIndexObj.itemIndex];
      setActiveSchedule(session);
    }
  }, [activeIndexObj]);

  useEffect(() => {
    init();
  }, [activeIndexObj.itemIndex, init]);

  useEffect(() => {
    let foundNextSession = false;
    groupedSchedules.forEach((data: IScheduleGroupByDate, parentIndex: number) => {
      if (!foundNextSession) {
        data.events.forEach((event: ISchedule, itemIndex: number) => {
          if (!foundNextSession) {
            if (moment().isSameOrBefore(event.meta.slot.from)) {
              setActiveIndexObj({ parentIndex, itemIndex });
              foundNextSession = true;
            }
          }
        });
      }
    });

    //set durations
    const mostWeekDay = [0, 0, 0, 0, 0, 0, 0];
    let totalSessionDurations = 0;
    let totalMeetingDurations = 0;
    groupedSchedules.forEach((data: IScheduleGroupByDate) => {
      mostWeekDay[moment(data.date, 'YYYY-MM-DD').weekday()] += 1;
      data.events.forEach((event: ISchedule) => {
        totalSessionDurations += moment(event.meta.slot.to, 'hh:mm A').diff(moment(event.meta.slot.from, 'hh:mm A'), 'minutes');
        totalMeetingDurations += event?.meetingDuration ?? 0;
      });
    });

    setAvarageDuration(Math.round(totalSessionDurations / groupedSchedules.length));
    setSpentDuration(totalMeetingDurations);
    setMostSessionDay(moment().day(Math.max(...mostWeekDay)).format('dddd'));
    setTotalDuration(totalSessionDurations);
  }, [groupedSchedules]);

  // success modal
  const [cancelSuccessModal, setCancelSuccessModal] = useState<boolean>(false);
  const handleSuccessModalClose = () => {
    setCancelSuccessModal(false);
    window.location.reload();
  };
  const handleCancelSession = async () => {
    const dateFrom = moment()
      .subtract(activeMonthIndex, 'months')
      .startOf('month')
      .format('YYYY-MM-DD');
    const dateTo = moment()
      .subtract(activeMonthIndex, 'months')
      .endOf('month')
      .format('YYYY-MM-DD');
    try {
      await new Promise((resolve, reject) => {
        dispatch(schedulesActions.cancelSession({
          id: activeSchedule.id,
          meetingId: activeSchedule.meetingId,
          resolve,
          reject
        }));
        setTitle(moment().subtract(activeMonthIndex, 'months').format('MMMM, YYYY'));
        schedulesActions.getScheduleList({ from: dateFrom, to: dateTo });
        handleCancelModelClose();
        setCancelSuccessModal(true);
      });
    } catch (error) {
      handleCancelModelClose();
      confirm({
        variant: 'info',
        icon: 'error',
        message: 'Something went wrong. Please try again',
        confirmButtonTxt: 'Okay',
      });
    }
  };

  useEffect(() => {
    async function fetchCalendarConfig() {
      if (activeSchedule) {
        const config = await getCalendarConfig(activeSchedule, activeSchedule?.meta?.slot, enterprise.name);
        setCalendarConfig(config);
      }
    }
    fetchCalendarConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSchedule]);

  function renderSchedule() {
    return (
      <>
        <Box
          borderRadius={1.5}
          sx={{
            p: 3,
            boxShadow: '0px 8px 60px #00000005',
            backgroundColor: 'background.paper',
            mb: 3,
          }}
        >
          <Typography variant="body2" sx={{ mb: 1 }}>
            {`${activeSchedule?.meta?.slot?.from &&
              moment(new Date(activeSchedule?.meta?.slot?.from)).format('MMM Do')
              }, 
                        ${activeSchedule?.meta?.slot?.from &&
              moment(new Date(activeSchedule?.meta?.slot?.from)).format('hh:mm a')
              } 
                        - ${activeSchedule?.meta?.slot?.to &&
              moment(new Date(activeSchedule?.meta?.slot?.to)).format('hh:mm a')
              }`}
          </Typography>

          <div className="htmlDescription mb-3" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(activeSchedule?.description && activeSchedule.description.length > 0 ? activeSchedule.description : '') }} />

          <SecondaryBadge>{activeSchedule?.sessionType}</SecondaryBadge>

          <Box className="d-flex align-items-center" sx={{ my: 3 }}>
            <AvatarGroup max={3}>
              {activeSchedule?.purchaseSummary?.latestBookedUsers.map((user: any) => (
                <Avatar
                  key={user?.id}
                  sx={[avatarStyle]}
                  alt={user?.name}
                  src={user?.avatar}
                >
                  {user?.name.charAt(0)}
                </Avatar>
              ))}
            </AvatarGroup>

            <Typography variant="body2" sx={{ color: 'text.secondary', ml: 1 }}>
              {activeSchedule?.purchaseSummary?.totalBookedCount > 0
                ? `${activeSchedule?.purchaseSummary?.totalBookedCount} users have booked this session`
                : 'Still, no one booked this session'}
            </Typography>
          </Box>

          {!activeSchedule?.isCompleted && activeSchedule.recordings.length === 0 && (
            <>
              {!activeSchedule.isCancel && (
                <>
                  {joinAction.ctaText === "Upcoming" ? (
                    <>
                      <Tooltip
                        title="Your session will be open 5 minutes before the start time"
                        placement="bottom-start"
                        arrow={false}
                      >
                        <span>
                          <PrimaryLoadingButton
                            onClick={loadUrl}
                            size="large"
                            fullWidth
                            type="button"
                            loading={isUrlLoading}
                            disabled={joinAction.isDisabled}
                          >
                            {joinAction.ctaText}
                          </PrimaryLoadingButton>
                        </span>
                      </Tooltip>
                      <SecondaryButton onClick={() => { setOpenCalendarModal(true); }}
                        sx={{ mt: 1 }}
                        size="large"
                        fullWidth
                        type="button">
                        Add to calendar
                      </SecondaryButton>
                    </>
                  ) : (
                    <PrimaryLoadingButton
                      onClick={loadUrl}
                      size="large"
                      fullWidth
                      type="button"
                      loading={isUrlLoading}
                      disabled={joinAction.isDisabled}
                    >
                      {joinAction.ctaText}
                    </PrimaryLoadingButton>
                  )}
                  {joinAction.ctaText === "Upcoming" && (
                    <Box
                      className="d-flex align-items-center justify-content-center"
                      sx={{ cursor: 'pointer' }}
                      onClick={handleCancelModelOpen}
                    >
                      <VideocamOffOutlinedIcon sx={{ mt: 1, mr: 1, color: 'red' }} />
                      <Typography
                        variant="body2"
                        sx={{ color: 'red', mt: 1, }}
                      >
                        Cancel session on this date
                      </Typography>
                    </Box>
                  )}
                </>
              )}
              {activeSchedule.isCancel && (
                <Box className="d-flex align-items-center justify-content-center ">
                  <VideocamOffOutlinedIcon sx={{ mt: 1, mr: 1, color: '#999999' }} />
                  <Typography
                    variant="body2"
                    sx={{ color: '#999999', mt: 1, }}
                  >
                    This session has been cancelled
                  </Typography>
                </Box>
              )}
            </>
          )}

          {activeSchedule &&
            activeSchedule.recordings &&
            activeSchedule.recordings.map((recording: IRecording, index: number) => (
              <SecondaryLoadingButton
                key={index}
                onClick={() => {
                  window.open(recording.path, '_blank');
                }}
                size="small"
                fullWidth
                type="button"
                sx={{ mt: 2 }}
              >
                {`View recording ${activeSchedule.recordings.length > 1 ? index + 1 : ''
                  }`}
              </SecondaryLoadingButton>
            ))}
        </Box>
      </>
    )
  }

  return (
    <ExpertHubLayout>
      <div className="container">
        <div className="row">
          <Box
            className="col-12 col-md-12 col-lg-8 pe-lg-4"
            sx={{
              ['@media (min-width:780px)']: {
                // eslint-disable-line no-useless-computed-key
                borderRight: 1,
                borderRightColor: 'primary.light',
              },
              mb: 3,
            }}
          >
            <div className="d-flex align-items-center">
              <Typography variant="h4" sx={{ mb: 0, mr: 3 }}>
                {title}
              </Typography>
              <IconButton
                onClick={() => {
                  handleMonth(1);
                }}
              >
                <ArrowCircleLeft />
              </IconButton>
              <IconButton
                onClick={() => {
                  handleMonth(-1);
                }}
              >
                <ArrowCircleRight />
              </IconButton>
            </div>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
              {`Today is ${subTitle}`}
            </Typography>

            <div className="col-12">
              <Loader loading={loading}>
                {groupedSchedules.map((groupedData: any, index: number) => (
                  <SchedulesListItem
                    key={`${Math.random()}-${groupedData?.date}`}
                    activeIndexObject={activeIndexObj}
                    onClick={handleActive}
                    index={index}
                    groupedData={groupedData}
                  />
                ))}
              </Loader>
            </div>
          </Box>

          <div className="col-12 col-md-12 col-lg-4 ps-xl-4 pt-2">
            <Box component="div" ref={stickyRef} className="scheduleSideBar" display={{ md: 'block', xs: 'none' }} >
              {!loading && activeSchedule && groupedSchedules && groupedSchedules.length > 0 && (
                <>
                  <Box
                    borderRadius={1.5}
                    sx={{
                      p: 3,
                      boxShadow: '0px 8px 60px #00000005',
                      backgroundColor: 'background.paper',
                      mb: 3,
                    }}
                  >
                    <Typography variant="body2" sx={{ mb: 3 }}>
                      Next session overview
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {activeSchedule?.title}
                    </Typography>
                    {renderSchedule()}
                  </Box>
                </>
              )}
            </Box>
          </div>
          {!loading && groupedSchedules && groupedSchedules.length === 0 && (
            <div className="my-3">
              <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                There is no sessions in this month
              </Typography>
            </div>
          )}
        </div>
      </div>
      {activeSchedule && joinAction.ctaText === "Upcoming" && (
        <>
          {/* // Confirm modal  */}
          <ClosableModal
            title="Are you sure you want to cancel this session?"
            subTitle=""
            modalClass="cancel-session-modal"
            open={openCancelModel}
            onClose={handleCancelModelClose}
          >
            <Box
              borderRadius={1.5}
              sx={{
                p: 3,
                boxShadow: '0px 8px 60px #0000005',
                backgroundColor: 'background.paper',
                mb: 2,
                borderColor: 'primary.dark',
                borderWidth: '0.5px',
                borderStyle: 'solid',
              }}
            >
              <Typography component="div" variant="h6" sx={{ mb: 1 }}>
                {activeSchedule?.title}
              </Typography>
              <Typography component="div" variant="body2" sx={labelStyle} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(activeSchedule?.description && activeSchedule.description.length > 0 ? activeSchedule.description : '') }}>
              </Typography>
              <Box className="d-flex flex-column flex-sm-row align-items-center" sx={{ mb: 1 }}>
                <SecondaryBadge sx={{ mr: 2 }}>{activeSchedule?.sessionType}</SecondaryBadge>
                <Typography component="div" variant="body2" sx={{ mt: 1 }}>
                  {`${activeSchedule?.meta?.slot?.from &&
                    moment(new Date(activeSchedule?.meta?.slot?.from)).format(
                      'MMM Do',
                    )
                    }, 
                              ${activeSchedule?.meta?.slot?.from &&
                    moment(new Date(activeSchedule?.meta?.slot?.from)).format('YYYY')
                    }
                              \u00A0\u00A0\u00A0\u00A0
                              ${activeSchedule?.meta?.slot?.from &&
                    moment(new Date(activeSchedule?.meta?.slot?.from)).format('hh:mm')
                    } 
                              - ${activeSchedule?.meta?.slot?.to &&
                    moment(new Date(activeSchedule?.meta?.slot?.to)).format('hh:mm a')
                    }`}
                </Typography>
              </Box>
            </Box>
            <Typography variant="body2" sx={labelStyle}>
              Cancelling this session may trigger a refund.
            </Typography>
            <Box
              className="d-flex align-items-center"
              sx={{ cursor: 'pointer' }}
              onClick={handleCancelSession}
            >
              <VideocamOffOutlinedIcon sx={{ mt: 1, mr: 1, color: 'red' }} />
              <Typography
                variant="body2"
                sx={{ color: 'red', mt: 1, }}
              >
                Yes, cancel session on this date
              </Typography>
            </Box>
          </ClosableModal>
          {/* // Cancel success modal  */}
          <CustomModal
            open={cancelSuccessModal}
            onClose={handleSuccessModalClose}
            modalClass="cancellation-confirm-modal"
          >
            <Box>
              <div className="col-md-12">
                <Typography variant="h5" mb={1}>
                  {' '}
                  This session has been cancelled.{' '}
                </Typography>
              </div>
              <Typography variant="body2" sx={labelStyle}>
                Your session {activeSchedule?.sessionType} scheduled on{' '}
                {activeSchedule?.meta?.slot?.from &&
                  moment(new Date(activeSchedule?.meta?.slot?.from)).format(
                    'Do',
                  )}{' '}
                of {' '} <br></br>
                {activeSchedule?.meta?.slot?.from &&
                  moment(new Date(activeSchedule?.meta?.slot?.from)).format(
                    'MMM',
                  )}{' '}
                {activeSchedule?.meta?.slot?.from &&
                  moment(new Date(activeSchedule?.meta?.slot?.from)).format('YYYY')}
                , from{' '}
                {activeSchedule?.meta?.slot?.from &&
                  moment(new Date(activeSchedule?.meta?.slot?.from)).format(
                    'hh:mm a',
                  )}{' '}
                to{' '}
                {activeSchedule?.meta?.slot?.to &&
                  moment(new Date(activeSchedule?.meta?.slot?.to)).format(
                    'hh:mm a',
                  )}{' '}
                has been <br></br> cancelled.
              </Typography>
              <Box className="d-flex ">
                <SecondaryLoadingButton
                  loading={loading}
                  fullWidth
                  type="submit"
                  onClick={handleSuccessModalClose}
                >
                  Go back to schedule
                </SecondaryLoadingButton>
              </Box>
            </Box>
          </CustomModal>
          <AddToCalendarModal
            open={openCalendarModal}
            onClose={() => setOpenCalendarModal(false)}
            config={calendarConfig}
          />
        </>
      )}
      <Box sx={{ display: { md: 'none', xs: 'block' } }}>
        <ClosableModal open={open} onClose={handleClose} title="Next session overview" subTitle={activeSchedule?.title}>
          {!loading && activeSchedule && groupedSchedules && groupedSchedules.length > 0 && (
            renderSchedule()
          )}
        </ClosableModal>
      </Box>
    </ExpertHubLayout>
  );
};

export default SchedulesView;
